import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Input } from '../../components/ui/Kit';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import { api } from '../../lib/api';

const {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiLink,
  FiList,
  FiMenu,
  FiExternalLink,
  FiFileText,
  FiBriefcase,
  FiArrowUp,
  FiArrowDown,
} = FiIcons;

const STATE_OPTIONS = [
  { value: 'ALL', label: 'All States' },
  { value: 'NJ', label: 'NJ' },
  { value: 'NY', label: 'NY' },
];

const PHASE_OPTIONS = [
  { value: 'education', label: 'Education' },
  { value: 'exam', label: 'Exam' },
  { value: 'fingerprinting', label: 'Fingerprinting' },
  { value: 'application', label: 'Application' },
  { value: 'contracting', label: 'Contracting' },
  { value: 'manage_license', label: 'Manage Your License' },
  { value: 'compliance', label: 'Compliance & CE' },
];

const PHASE_ORDER = PHASE_OPTIONS.map((p) => p.value);

const WRITE_ROLES = ['admin', 'super_admin'];

function phaseLabel(phase) {
  return PHASE_OPTIONS.find((p) => p.value === phase)?.label || 'Unassigned';
}

function ToggleSwitch({ checked, onChange, disabled, id }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function formatStates(states) {
  if (!states?.length || states.includes('ALL')) return 'All States';
  return states.join(', ');
}

function normalizeLinks(links) {
  if (!Array.isArray(links)) return [];
  return links.map((l) => ({
    label: typeof l?.label === 'string' ? l.label : '',
    url: typeof l?.url === 'string' ? l.url : '',
  }));
}

function emptyForm(category) {
  return {
    category,
    title: '',
    subtitle: '',
    phase: category === 'licensing_step' ? 'education' : null,
    step_order: 0,
    instructions: '',
    url: '',
    links: [{ label: '', url: '' }],
    applies_to_states: ['ALL'],
    is_active: true,
  };
}

function ResourceFormModal({ isOpen, onClose, initial, category, agencyId, nextStepOrderForPhase, canWrite, onSaved }) {
  const [form, setForm] = useState(emptyForm(category));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isStep = category === 'licensing_step';
  const isProvider = category === 'provider';

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (initial) {
      const existingLinks = normalizeLinks(initial.links);
      setForm({
        category: initial.category || category,
        title: initial.title ?? '',
        subtitle: initial.subtitle ?? '',
        phase: initial.phase ?? (isStep ? 'education' : null),
        step_order: initial.step_order ?? 0,
        instructions: initial.instructions ?? '',
        url: initial.url ?? '',
        links: existingLinks.length ? existingLinks : [{ label: '', url: '' }],
        applies_to_states: Array.isArray(initial.applies_to_states) && initial.applies_to_states.length
          ? [...initial.applies_to_states]
          : ['ALL'],
        is_active: initial.is_active !== false,
      });
    } else {
      const defaultPhase = isStep ? 'education' : null;
      setForm({
        ...emptyForm(category),
        phase: defaultPhase,
        step_order: isStep ? nextStepOrderForPhase(defaultPhase) : 0,
      });
    }
  }, [isOpen, initial, category, isStep, nextStepOrderForPhase]);

  const setField = (field) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
  };

  const toggleState = (value) => {
    setForm((prev) => {
      const current = prev.applies_to_states || [];
      if (value === 'ALL') {
        return { ...prev, applies_to_states: ['ALL'] };
      }
      const withoutAll = current.filter((s) => s !== 'ALL');
      const has = withoutAll.includes(value);
      const next = has ? withoutAll.filter((s) => s !== value) : [...withoutAll, value];
      return { ...prev, applies_to_states: next.length ? next : ['ALL'] };
    });
  };

  const updateLink = (index, field, value) => {
    setForm((prev) => {
      const links = [...(prev.links || [])];
      links[index] = { ...links[index], [field]: value };
      return { ...prev, links };
    });
  };

  const addLinkRow = () => {
    setForm((prev) => ({
      ...prev,
      links: [...(prev.links || []), { label: '', url: '' }],
    }));
  };

  const removeLinkRow = (index) => {
    setForm((prev) => {
      const links = (prev.links || []).filter((_, i) => i !== index);
      return { ...prev, links: links.length ? links : [{ label: '', url: '' }] };
    });
  };

  const moveLinkRow = (index, direction) => {
    setForm((prev) => {
      const links = [...(prev.links || [])];
      const target = index + direction;
      if (target < 0 || target >= links.length) return prev;
      [links[index], links[target]] = [links[target], links[index]];
      return { ...prev, links };
    });
  };

  const modalTitle = (() => {
    if (initial?.id) {
      if (isStep) return 'Edit Licensing Step';
      if (isProvider) return 'Edit Product Provider';
      return 'Edit Resource Link';
    }
    if (isStep) return 'Add Licensing Step';
    if (isProvider) return 'Add Product Provider';
    return 'Add Resource Link';
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) return;
    if (!form.title.trim()) {
      setError(isProvider ? 'Provider name is required' : 'Title is required');
      return;
    }

    let cleanedLinks = [];
    if (isProvider) {
      cleanedLinks = normalizeLinks(form.links)
        .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
        .filter((l) => l.label || l.url);
      const incomplete = cleanedLinks.find((l) => !l.label || !l.url);
      if (incomplete) {
        setError('Each provider link needs both a label and a URL');
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        agency_id: agencyId,
        category,
        title: form.title.trim(),
        subtitle: isProvider ? (form.subtitle.trim() || null) : null,
        phase: isStep ? (form.phase || null) : null,
        step_order: isStep ? Number(form.step_order) || 0 : 0,
        instructions: form.instructions.trim() || null,
        url: isProvider ? null : (form.url.trim() || null),
        links: isProvider ? cleanedLinks : [],
        applies_to_states: isProvider
          ? ['ALL']
          : form.applies_to_states?.includes('ALL')
            ? ['ALL']
            : form.applies_to_states?.length
              ? form.applies_to_states
              : ['ALL'],
        is_active: form.is_active,
      };
      if (isStep && !payload.phase) {
        setError('Phase is required for licensing steps');
        setSaving(false);
        return;
      }
      if (initial?.id) {
        await api.licensingResources.update(initial.id, payload);
      } else {
        await api.licensingResources.create(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save resource');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title={modalTitle}
      className="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 mt-2">
        <Input
          label={isProvider ? 'Provider name' : 'Title'}
          value={form.title}
          onChange={setField('title')}
          placeholder={
            isStep
              ? 'e.g. Fingerprinting appointment'
              : isProvider
                ? 'e.g. F&G'
                : 'e.g. State DOI portal'
          }
          disabled={saving || !canWrite}
          required
        />

        {isProvider && (
          <Input
            label="Subtitle"
            value={form.subtitle}
            onChange={setField('subtitle')}
            placeholder="e.g. Life Insurance"
            disabled={saving || !canWrite}
          />
        )}

        {isStep && (
          <>
            <div>
              <label htmlFor="resource-phase" className="block text-sm font-medium text-gray-700 mb-1.5">
                Phase
              </label>
              <select
                id="resource-phase"
                className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                value={form.phase || ''}
                disabled={saving || !canWrite}
                required
                onChange={(e) => {
                  const phase = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    phase,
                    step_order: initial?.id ? prev.step_order : nextStepOrderForPhase(phase),
                  }));
                }}
              >
                {PHASE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1.5">
                Change phase here — drag-reorder only moves steps within the same phase.
              </p>
            </div>
            <Input
              label="Order within phase"
              type="number"
              min={0}
              value={form.step_order}
              onChange={setField('step_order')}
              disabled={saving || !canWrite}
            />
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {isProvider ? 'Description' : 'Instructions'}
          </label>
          <textarea
            className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[96px] resize-y disabled:opacity-50 disabled:cursor-not-allowed"
            value={form.instructions}
            onChange={setField('instructions')}
            disabled={saving || !canWrite}
            placeholder={
              isProvider
                ? 'e.g. insurance solutions for life and retirement'
                : 'Per-state guidance shown to agents'
            }
          />
        </div>

        {!isProvider && (
          <Input
            label="URL (optional)"
            type="url"
            value={form.url}
            onChange={setField('url')}
            placeholder="https://"
            disabled={saving || !canWrite}
          />
        )}

        {isProvider && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="block text-sm font-medium text-gray-700">Links</p>
              {canWrite && (
                <Button type="button" variant="ghost" size="sm" onClick={addLinkRow} disabled={saving}>
                  <SafeIcon icon={FiPlus} className="mr-1 w-3.5 h-3.5" />
                  Add link
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {(form.links || []).map((link, index) => (
                <div
                  key={index}
                  className="rounded-md border border-gray-200 bg-gray-50/50 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-gray-500">Link {index + 1}</span>
                    {canWrite && (
                      <div className="inline-flex gap-0.5">
                        <button
                          type="button"
                          className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-white disabled:opacity-30"
                          disabled={saving || index === 0}
                          onClick={() => moveLinkRow(index, -1)}
                          aria-label="Move link up"
                        >
                          <SafeIcon icon={FiArrowUp} className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-white disabled:opacity-30"
                          disabled={saving || index === (form.links?.length || 0) - 1}
                          onClick={() => moveLinkRow(index, 1)}
                          aria-label="Move link down"
                        >
                          <SafeIcon icon={FiArrowDown} className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-white"
                          disabled={saving}
                          onClick={() => removeLinkRow(index)}
                          aria-label="Remove link"
                        >
                          <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <Input
                    label="Label"
                    value={link.label}
                    onChange={(e) => updateLink(index, 'label', e.target.value)}
                    placeholder="e.g. Agent Portal"
                    disabled={saving || !canWrite}
                  />
                  <Input
                    label="URL"
                    type="url"
                    value={link.url}
                    onChange={(e) => updateLink(index, 'url', e.target.value)}
                    placeholder="https://"
                    disabled={saving || !canWrite}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {!isProvider && (
          <div>
            <p className="block text-sm font-medium text-gray-700 mb-1.5">Applies to states</p>
            <div className="flex flex-wrap gap-3">
              {STATE_OPTIONS.map((opt) => {
                const checked = form.applies_to_states?.includes(opt.value)
                  || (opt.value === 'ALL' && form.applies_to_states?.includes('ALL'));
                return (
                  <label
                    key={opt.value}
                    className={`inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border cursor-pointer ${
                      checked ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-700'
                    } ${!canWrite || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                      checked={!!checked}
                      disabled={saving || !canWrite}
                      onChange={() => toggleState(opt.value)}
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Choose All States, or one or more specific states (NJ / NY).
            </p>
          </div>
        )}

        <div className="flex items-center gap-3">
          <ToggleSwitch
            id="resource-active"
            checked={form.is_active}
            onChange={(v) => setForm((prev) => ({ ...prev, is_active: v }))}
            disabled={saving || !canWrite}
          />
          <label htmlFor="resource-active" className="text-sm text-gray-700">
            Active
          </label>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {canWrite && (
            <Button type="submit" size="sm" variant="primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner className="mr-2" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default function LicensingResourcesTab() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const agencyId = user?.agency_id;
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formCategory, setFormCategory] = useState('licensing_step');
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    if (!agencyId) {
      setResources([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await api.licensingResources.getAll(agencyId);
      setResources(rows);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not load licensing resources', message: err.message });
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [agencyId, addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const steps = useMemo(
    () =>
      resources
        .filter((r) => r.category === 'licensing_step')
        .sort((a, b) => {
          const ai = PHASE_ORDER.indexOf(a.phase);
          const bi = PHASE_ORDER.indexOf(b.phase);
          const aPhase = ai === -1 ? PHASE_ORDER.length : ai;
          const bPhase = bi === -1 ? PHASE_ORDER.length : bi;
          if (aPhase !== bPhase) return aPhase - bPhase;
          return (a.step_order ?? 0) - (b.step_order ?? 0);
        }),
    [resources],
  );

  const stepsByPhase = useMemo(() => {
    const groups = PHASE_ORDER.map((phase) => ({
      phase,
      label: phaseLabel(phase),
      rows: steps.filter((s) => s.phase === phase),
    }));
    const unassigned = steps.filter((s) => !s.phase || !PHASE_ORDER.includes(s.phase));
    if (unassigned.length) {
      groups.push({ phase: null, label: 'Unassigned', rows: unassigned });
    }
    return groups.filter((g) => g.rows.length > 0);
  }, [steps]);

  const links = useMemo(
    () =>
      resources
        .filter((r) => r.category === 'general_link')
        .sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0) || (a.title || '').localeCompare(b.title || '')),
    [resources],
  );

  const providers = useMemo(
    () =>
      resources
        .filter((r) => r.category === 'provider')
        .sort((a, b) => (a.title || '').localeCompare(b.title || '')),
    [resources],
  );

  const nextStepOrderForPhase = useCallback(
    (phase) => {
      const inPhase = steps.filter((s) => s.phase === phase);
      if (!inPhase.length) return 1;
      return Math.max(...inPhase.map((s) => s.step_order ?? 0)) + 1;
    },
    [steps],
  );

  const openCreate = (category) => {
    setFormCategory(category);
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setFormCategory(row.category);
    setEditing(row);
    setFormOpen(true);
  };

  const handleToggleActive = async (row) => {
    if (!canWrite) return;
    setTogglingId(row.id);
    try {
      await api.licensingResources.update(row.id, { is_active: !row.is_active });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !canWrite) return;
    setDeleting(true);
    try {
      await api.licensingResources.delete(deleteTarget.id);
      addToast({ type: 'success', title: 'Resource deleted' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  const persistPhaseStepOrder = async (phase, ordered) => {
    setReordering(true);
    const items = ordered.map((row, i) => ({ id: row.id, step_order: i + 1 }));
    setResources((prev) =>
      prev.map((r) => {
        if (r.category !== 'licensing_step') return r;
        const idx = ordered.findIndex((row) => row.id === r.id);
        if (idx < 0) return r;
        return { ...r, step_order: idx + 1, phase: phase ?? r.phase };
      }),
    );
    try {
      await api.licensingResources.reorder(items);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not reorder steps', message: err.message });
      await load();
    } finally {
      setReordering(false);
    }
  };

  const handleDropOnStep = async (targetId, phase) => {
    if (!canWrite || !dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const phaseRows = steps.filter((s) =>
      phase == null ? !s.phase || !PHASE_ORDER.includes(s.phase) : s.phase === phase,
    );
    const fromIdx = phaseRows.findIndex((s) => s.id === dragId);
    const toIdx = phaseRows.findIndex((s) => s.id === targetId);
    setDragId(null);
    // Only allow reorder within the same phase group
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...phaseRows];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    await persistPhaseStepOrder(phase, next);
  };

  return (
    <div className="space-y-8">
      {!canWrite && (
        <div className="rounded-md border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          View-only for managers. Only Admins and Super Admins can add, edit, reorder, or delete
          licensing resources.
        </div>
      )}

      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={FiList} className="text-primary w-5 h-5" />
              Licensing Steps
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Checklist grouped by phase. Drag to reorder within a phase; change phase in the
              edit modal. Supports per-state instructions (NJ vs NY).
            </p>
          </div>
          {canWrite && (
            <Button size="sm" variant="primary" onClick={() => openCreate('licensing_step')}>
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add Step
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-gray-500 justify-center">
            <Spinner className="text-primary" />
            Loading…
          </div>
        ) : steps.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
            <SafeIcon icon={FiList} className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No licensing steps yet</p>
            {canWrite && (
              <Button size="sm" className="mt-4" variant="primary" onClick={() => openCreate('licensing_step')}>
                <SafeIcon icon={FiPlus} className="mr-2" />
                Add Step
              </Button>
            )}
          </div>
        ) : (
          <div className={`space-y-6 ${reordering ? 'opacity-70 pointer-events-none' : ''}`}>
            {stepsByPhase.map((group) => (
              <div key={group.phase ?? 'unassigned'} className="space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">{group.label}</h3>
                  <span className="text-xs text-gray-400">
                    {group.rows.length} step{group.rows.length === 1 ? '' : 's'}
                  </span>
                  {group.phase == null && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded">
                      Assign a phase via Edit
                    </span>
                  )}
                </div>
                {group.rows.map((row) => (
                  <div
                    key={row.id}
                    draggable={canWrite}
                    onDragStart={() => canWrite && setDragId(row.id)}
                    onDragOver={(e) => {
                      if (canWrite) e.preventDefault();
                    }}
                    onDrop={() => void handleDropOnStep(row.id, group.phase)}
                    className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:border-gray-300 transition-colors"
                  >
                    {canWrite && (
                      <button
                        type="button"
                        className="mt-1 cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                        aria-label="Drag to reorder within phase"
                      >
                        <SafeIcon icon={FiMenu} className="w-4 h-4" />
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-secondary bg-gray-100 px-2 py-0.5 rounded">
                          #{row.step_order ?? '—'}
                        </span>
                        <Badge variant="default">{phaseLabel(row.phase)}</Badge>
                        <Badge variant="default">{formatStates(row.applies_to_states)}</Badge>
                        {row.instructions ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <SafeIcon icon={FiFileText} className="w-3 h-3" />
                            Instructions
                          </span>
                        ) : null}
                        {row.url ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                            Link
                          </span>
                        ) : null}
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">{row.title}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <ToggleSwitch
                        id={`step-active-${row.id}`}
                        checked={row.is_active !== false}
                        onChange={() => void handleToggleActive(row)}
                        disabled={!canWrite || togglingId === row.id}
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(row)}>
                        <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                      </Button>
                      {canWrite && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteTarget(row)}>
                          <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={FiLink} className="text-primary w-5 h-5" />
              General Resource Links
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Helpful portals and documents agents can open anytime.
            </p>
          </div>
          {canWrite && (
            <Button size="sm" variant="primary" onClick={() => openCreate('general_link')}>
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add Link
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-gray-500 justify-center">
            <Spinner className="text-primary" />
            Loading…
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
            <SafeIcon icon={FiLink} className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No general links yet</p>
            {canWrite && (
              <Button size="sm" className="mt-4" variant="primary" onClick={() => openCreate('general_link')}>
                <SafeIcon icon={FiPlus} className="mr-2" />
                Add Link
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-500">Title</th>
                  <th className="px-4 py-3 font-medium text-gray-500">URL</th>
                  <th className="px-4 py-3 font-medium text-gray-500">States</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Active</th>
                  <th className="px-4 py-3 font-medium text-gray-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.title}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate">
                      {row.url ? (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {row.url}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatStates(row.applies_to_states)}</td>
                    <td className="px-4 py-3">
                      <ToggleSwitch
                        id={`link-active-${row.id}`}
                        checked={row.is_active !== false}
                        onChange={() => void handleToggleActive(row)}
                        disabled={!canWrite || togglingId === row.id}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          className="p-2 rounded-md text-gray-500 hover:text-primary hover:bg-gray-100"
                          title="Edit"
                          onClick={() => openEdit(row)}
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        {canWrite && (
                          <button
                            type="button"
                            className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50"
                            title="Delete"
                            onClick={() => setDeleteTarget(row)}
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={FiBriefcase} className="text-primary w-5 h-5" />
              Product Providers
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Marketplace carriers (F&amp;G, Ameritas, Ethos, BMI, etc.) with multiple labeled
              portal links.
            </p>
          </div>
          {canWrite && (
            <Button size="sm" variant="primary" onClick={() => openCreate('provider')}>
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add Provider
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-gray-500 justify-center">
            <Spinner className="text-primary" />
            Loading…
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
            <SafeIcon icon={FiBriefcase} className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No product providers yet</p>
            {canWrite && (
              <Button size="sm" className="mt-4" variant="primary" onClick={() => openCreate('provider')}>
                <SafeIcon icon={FiPlus} className="mr-2" />
                Add Provider
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {providers.map((row) => {
              const providerLinks = normalizeLinks(row.links).filter((l) => l.label || l.url);
              return (
                <div
                  key={row.id}
                  className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:border-gray-300 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {row.subtitle ? <Badge variant="primary">{row.subtitle}</Badge> : null}
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <SafeIcon icon={FiLink} className="w-3 h-3" />
                        {providerLinks.length} link{providerLinks.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">{row.title}</p>
                    {row.instructions ? (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{row.instructions}</p>
                    ) : null}
                    {providerLinks.length > 0 && (
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                        {providerLinks.map((l, i) => (
                          <a
                            key={`${row.id}-link-${i}`}
                            href={l.url || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline truncate max-w-[200px]"
                            onClick={(e) => {
                              if (!l.url) e.preventDefault();
                            }}
                          >
                            {l.label || l.url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <ToggleSwitch
                      id={`provider-active-${row.id}`}
                      checked={row.is_active !== false}
                      onChange={() => void handleToggleActive(row)}
                      disabled={!canWrite || togglingId === row.id}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(row)}>
                      <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                    </Button>
                    {canWrite && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteTarget(row)}>
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <ResourceFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editing}
        category={formCategory}
        agencyId={agencyId}
        nextStepOrderForPhase={nextStepOrderForPhase}
        canWrite={canWrite}
        onSaved={() => {
          addToast({
            type: 'success',
            title: editing ? 'Resource updated' : 'Resource created',
          });
          void load();
        }}
      />

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete resource?"
        className="max-w-md"
      >
        <p className="text-sm text-gray-600 mt-2">
          Remove <span className="font-medium text-gray-900">{deleteTarget?.title}</span>? This
          cannot be undone.
        </p>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button size="sm" variant="danger" onClick={() => void handleDelete()} disabled={deleting}>
            {deleting ? <Spinner className="mr-2" /> : null}
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
